import { assertEvent, assign } from 'xstate';

import type {
  CartMachineAction,
  CartMachineContext,
  CartMachineGuard,
  CartMutation,
} from './Cart.machine.types';

// ─── ACTIONS ────────────────────────────────────────────────────────────────────

export const registerCartMutation: CartMachineAction = assign(({ context, event }) => {
  assertEvent(event, 'REGISTER_PENDING_MUTATION');

  const { mutationName } = event;
  const { pendingMutations } = context;

  const currentValue = pendingMutations[mutationName];

  return {
    pendingMutations: {
      ...pendingMutations,
      [mutationName]: currentValue + 1,
    },
  };
});

export const unregisterPendingCartMutation: CartMachineAction = assign(({ context, event }) => {
  assertEvent(event, 'UNREGISTER_PENDING_MUTATION');

  const { mutationName } = event;
  const { pendingMutations } = context;

  const currentValue = pendingMutations[mutationName];

  if (currentValue === 0) return {};

  return {
    pendingMutations: {
      ...pendingMutations,
      [mutationName]: currentValue - 1,
    },
  };
});

export const registerCartView: CartMachineAction = assign(({ context, event }) => {
  assertEvent(event, 'REGISTER_CART_VIEW');

  const { cartId } = event;
  const { cartViewCounts } = context;

  const currentValue = cartViewCounts[cartId] ?? 0;

  return {
    cartViewCounts: {
      ...cartViewCounts,
      [cartId]: currentValue + 1,
    },
  };
});

// ─── GUARDS ─────────────────────────────────────────────────────────────────────

export const checkIfHasPendingMutation: CartMachineGuard = ({ context }) => {
  const pendingMutationsTotal = getPendingMutationsTotal(context);

  return pendingMutationsTotal > 0;
};

export const checkIfFinishedFetching: CartMachineGuard = ({ context }) => {
  const pendingMutationsTotal = getPendingMutationsTotal(context);

  return pendingMutationsTotal === 0;
};

// ─── OTHER HELPERS ──────────────────────────────────────────────────────────────

export function getPendingMutationsNames(context: CartMachineContext) {
  const pendingMutations = Object.entries(context.pendingMutations);

  return pendingMutations.reduce((names, [name, mutationsNumber]) => {
    const cartMutationName = name as CartMutation;

    if (mutationsNumber > 0) return names.add(cartMutationName);

    return names;
  }, new Set<CartMutation>());
}

function getPendingMutationsTotal(context: CartMachineContext) {
  const pendingMutations = Object.values(context.pendingMutations);

  return pendingMutations.reduce((total, currentValue) => total + currentValue, 0);
}
